import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * A crash barrier around a lens.
 *
 * Artifact bodies are authored by the agent and can hold anything, so a shape
 * this build has never seen is a normal event, not an exceptional one. When one
 * view cannot render, the rest of the app — capture above all — has to keep
 * working, so the failure is contained here rather than blanking the screen.
 */

interface Props {
  children: ReactNode
  /** Named in the message, so "Story" beats "something". */
  label?: string
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[aurora] view crashed', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="page">
        <div className="empty section" role="alert">
          <p className="empty-title">
            {this.props.label ? `${this.props.label} could not open` : 'This could not open'}
          </p>
          <p className="empty-body">
            Nothing is lost: everything you captured is saved. Try again, and if it keeps
            happening, ask Sapling about it in the thread.
          </p>
          <div className="empty-action">
            <button className="btn btn-primary" onClick={() => this.setState({ error: null })}>
              Try again
            </button>
          </div>
          <details className="error-detail">
            <summary>Technical detail</summary>
            <pre>{error.message}</pre>
          </details>
        </div>
      </div>
    )
  }
}
